import { Link } from "react-router-dom";
import { Page, Card, Bar } from "../components/ui";
import { FACTOR_KEYS, FACTOR_WEIGHTS, CROWD_FULL_WEIGHT_AT } from "@shared/forecast-model";
import { usePageTitle } from "../lib/usePageTitle";
import { useT } from "../i18n";
import type { MessageKey } from "../i18n/en";

// Transparency page: spells out the projection model (the 8 weighted factors),
// how win % is derived, and how the reader poll is blended in. Weights and the
// crowd threshold come from @shared/forecast-model — the same numbers the worker
// runs — so this can never drift from the live model.
export default function HowItWorks() {
  const t = useT();
  usePageTitle(t("how.title"));

  // Heaviest factor sets the bar scale, so the bars read as relative weight.
  const maxWeight = Math.max(...Object.values(FACTOR_WEIGHTS));

  return (
    <Page>
      <h1 className="text-2xl font-blk mb-2">{t("how.title")}</h1>
      <p className="text-muted text-sm mb-6 max-w-[640px]">{t("how.intro")}</p>

      <Section heading={t("how.modelHeading")}>
        <p className="text-sm text-muted mb-4">{t("how.modelBody", { n: FACTOR_KEYS.length })}</p>
        <div className="text-[11px] uppercase tracking-wide text-muted mb-2">{t("how.factorsHeading")}</div>
        <div className="space-y-3">
          {FACTOR_KEYS.map((key) => {
            const weight = FACTOR_WEIGHTS[key];
            return (
              <div key={key}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-disp font-bold text-[15px]">
                    {t(`model.factor.${key}.label` as MessageKey)}
                  </span>
                  <span className="font-mono text-xs text-accent shrink-0">{t("how.weightLabel", { w: weight })}</span>
                </div>
                <Bar pct={(weight / maxWeight) * 100} />
                <p className="text-muted text-xs mt-1">{t(`model.factor.${key}.desc` as MessageKey)}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section heading={t("how.winHeading")}>
        <p className="text-sm text-muted">{t("how.winBody")}</p>
      </Section>

      <Section heading={t("how.blendHeading")}>
        <p className="text-sm text-muted mb-3">{t("how.blendBody", { n: CROWD_FULL_WEIGHT_AT })}</p>
        <code className="block bg-panel2 border border-border rounded-lg px-3 py-2 font-mono text-xs text-cyan overflow-x-auto">
          {t("how.blendFormula")}
        </code>
      </Section>

      <Section heading={t("how.confidenceHeading")}>
        <p className="text-sm text-muted">{t("how.confidenceBody")}</p>
      </Section>

      <Link to="/world" className="text-accent text-sm inline-block mt-2">
        {t("how.backToForecasts")} →
      </Link>
    </Page>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Card className="mb-4">
      <h2 className="font-disp font-bold text-[17px] mb-2">{heading}</h2>
      {children}
    </Card>
  );
}
